import React, { useCallback, useEffect, useRef, useState } from "react";
import Dashbard from "../../../Dashbard";
import {
  Accordion,
  AccordionSummary,
  Button,
  IconButton,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import GridSearchFilter, {
  AutoComplete,
  FilterAutoComplete,
  SelectedChiptItems,
  SelectedDateChip,
} from "../../../../../../components/common/FilterLayout/GridFilter";
import { useDebounce } from "../../../../../../utils/use-debounce";
import { ChipItem } from "../../../Conversations/ConverstionSettings/GridFilter";
import { FilterCreationDate } from "../../../EndUser";
import { ExpandMore, MoreHoriz } from "@material-ui/icons";
import { InputField, Pagination } from "../../../../../../components/common";
import { useDispatch, useSelector } from "react-redux";
import { emailTemplateActions } from "../../../../../../store/emailTemplate/emailTemplateActions";
import moment from "moment";
import EmailTemplateGrid from "./EmailTemplateGrid";
import AddEmailTemplateModal from "./AddEmailTemplateModal"; // Import the modal

const EmailTemplate = () => {
  const dispatch = useDispatch();
  let filtersIndex = 0;
  const filtersLimit = 4;
  const [isLoading, setLoading] = useState(false);
  const [emailTempData, setEmailTempData] = useState([]);
  const [filters, setFilters] = useState({
    title: "",
    subject: "",
    DateFrom: null,
    DateTo: null,
  });
  const { debouncedValue } = useDebounce(filters.name, 500);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState({
    orderByKey: "Id",
    orderVal: 0,
  });
  const [paginationResponse, setPaginationResponse] = useState({
    currentCount: 0,
    totalRecords: 0,
  });
  const [showFilters, setShowFilter] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [showFilterCounter, setShowFilterCounter] = useState(0);

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const filtersCount =
    (filters.title ? 1 : 0) +
    (filters.subject ? 1 : 0) +
    (filters.DateFrom ? 1 : 0) +
    (filters.DateTo ? 1 : 0);

  const onOrderBy = newOrder => {
    setOrder(newOrder);
  };

  const loadEmailTemplates = useCallback(() => {
    setLoading(true);
    dispatch(
      emailTemplateActions.onGetTemplatePaginatedData({
        pagination: {
          pageSize: rowsPerPage,
          pageNo: page,
          orderByKey: order.orderByKey,
          orderByVal: order.orderVal,
        },
        templateName: filters.title, // Changed from firstName to title
        subject: filters.subject, // Changed from lastName to subject
        body: "",
        DateFrom: filters.DateFrom
          ? moment(filters.DateFrom).format("MM/DD/yyyy")
          : null,
        DateTo: filters.DateTo
          ? moment(filters.DateTo).format("MM/DD/yyyy")
          : null,
        search: debouncedValue,
      })
    ).then(data => {
      if (data?.data?.success) {
        setEmailTempData(data?.data?.data?.data);
        setPaginationResponse(data?.data?.data?.pagination);
      } else {
        setEmailTempData([]);
        setPaginationResponse({
          currentCount: 0,
          totalRecords: 0,
        });
      }
      setLoading(false);
    });
  }, [
    dispatch,
    debouncedValue,
    page,
    rowsPerPage,
    order.orderByKey,
    order.orderVal,
    filters.title,
    filters.subject,
    filters.DateFrom,
    filters.DateTo,
  ]);

  useEffect(() => {
    loadEmailTemplates();
  }, [debouncedValue, page, rowsPerPage, order]);

  const showFilter = () => {
    window.scroll(0, 0);
    setIsFilter(true);
  };

  const hideFilter = () => {
    setIsFilter(false);
  };

  const applyFilters = () => {
    setShowFilterCounter(filtersCount);
    hideFilter();
    if (page === 1) {
      setLoading(true);
      dispatch(
        emailTemplateActions.onGetTemplatePaginatedData({
          pagination: {
            pageSize: rowsPerPage,
            pageNo: page,
            orderByKey: order.key,
            orderByVal: order.val,
          },
          templateName: filters.title, // Changed from firstName to title
          subject: filters.subject, // Changed from lastName to subject
          body: "",
          //   DateFrom: filters.DateFrom
          //     ? moment(filters.DateFrom).format("MM/DD/yyyy")
          //     : null,
          //   DateTo: filters.DateTo
          //     ? moment(filters.DateTo).format("MM/DD/yyyy")
          //     : null,
          //   search: debouncedValue,
        })
      ).then(data => {
        if (data?.data?.success) {
          setEmailTempData(data?.data?.data?.data);
          setPaginationResponse(data?.data?.data?.pagination);
        } else {
          setEmailTempData([]);
          setPaginationResponse({
            currentCount: 0,
            totalRecords: 0,
          });
        }
        setLoading(false);
      });
    }
  };

  const onClearButtonPress = () => {
    setLoading(true);
    setPage(1);
    setRowsPerPage(10);
    setShowFilterCounter(0);
    setFilters({
      title: "",
      subject: "",
      DateFrom: null,
      DateTo: null,
    });
    dispatch(
      emailTemplateActions.onGetTemplatePaginatedData({
        pagination: {
          pageSize: 10,
          pageNo: 1,
          orderByKey: "Id",
          orderByVal: 0,
        },
        templateName: "",
        subject: "",
        body: "",
        search: "",
        DateFrom: null,
        DateTo: null,
      })
    ).then(data => {
      if (data?.data?.success) {
        setEmailTempData(data?.data?.data?.data);
        setPaginationResponse(data?.data?.data?.pagination);
      } else {
        setEmailTempData([]);
        setPaginationResponse({
          currentCount: 0,
          totalRecords: 0,
        });
      }
      setLoading(false);
    });
  };

  // Handle Add Template button click
  const handleAddTemplateClick = () => {
    setSelectedTemplate(null);
    setAddModalOpen(true);
  };

  // Handle Edit Template button click (called from EmailTemplateGrid)
  const handleEditTemplateClick = template => {
    setSelectedTemplate(template);
    setEditModalOpen(true);
  };

  // Handle Delete Template (called from EmailTemplateGrid)
  const handleDeleteTemplate = async template => {
    if (
      window.confirm(
        `Are you sure you want to delete "${template.templateName}" template?`
      )
    ) {
      setModalLoading(true);
      try {
        const response = await dispatch(
          emailTemplateActions.onDeleteTemplate(template.id)
        );
        if (response?.data?.success) {
          // Show success message (you can use toast or snackbar)
          loadEmailTemplates(); // Refresh the list
          setTimeout(() => {
            alert("Template deleted successfully!");
          }, 2000);
        } else {
          alert(response?.data?.message || "Failed to delete template");
        }
      } catch (error) {
        console.error("Error deleting template:", error);
        alert("An error occurred while deleting the template");
      } finally {
        setModalLoading(false);
      }
    }
  };

  // Handle template saved (both add and edit)
  const handleTemplateSaved = () => {
    loadEmailTemplates(); // Refresh the list
    setAddModalOpen(false);
    setEditModalOpen(false);
  };

  return (
    <Dashbard>
      <div className='body-content'>
        <div className='contentHeading launchBtn'>
          <h1>Email Templates</h1>
          <Button
            color='primary'
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleAddTemplateClick}
            disabled={modalLoading}>
            Add Template
          </Button>
        </div>
        <div className='gridBody contentBody '>
          <GridSearchFilter
            isFilterAdded={true}
            setFilters={setFilters}
            filters={filters}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
            title={"Email Templates Filter"}
            isFilter={isFilter}
            showFilter={showFilter}
            hideFilter={hideFilter}
            applyFilters={applyFilters}
            showFilterCounter={showFilterCounter}>
            <Accordion
              defaultExpanded
              id='email-template-filter-expandable'
              TransitionProps={{ unmountOnExit: true }}>
              <AccordionSummary>
                <Typography>
                  <span>Active Filter(s)</span>
                  <span>
                    <Button
                      className='filterClearBtn filterClearBtnStyle'
                      variant='outlined'
                      onClick={() => {
                        hideFilter();
                        onClearButtonPress();
                      }}>
                      Clear
                    </Button>
                  </span>
                </Typography>
              </AccordionSummary>
              <div style={{ marginBottom: "10px" }}>
                {filtersCount <= 0 && (
                  <div className={`filtersChips`}>No Active Filter(s)</div>
                )}
              </div>

              <SelectedDateChip
                startDate={filters.DateFrom}
                endDate={filters.DateTo}
                setStartDate={date => {
                  setFilters(prevFilters => ({
                    ...prevFilters,
                    DateFrom: date,
                  }));
                }}
                setEndDate={date => {
                  setFilters(prevFilters => ({
                    ...prevFilters,
                    DateTo: date,
                  }));
                }}
                chipLabel={"Creation Date"}
              />
              {filters.title?.length > 0 &&
                (filtersIndex <= filtersLimit ||
                  (filtersIndex > filtersLimit && showFilters)) && (
                  <ChipItem
                    chipLabel='Title'
                    chipValue={filters.title}
                    onDelete={() => {
                      setFilters(prevFilters => ({
                        ...prevFilters,
                        title: "",
                      }));
                    }}
                  />
                )}
              {filters.subject?.length > 0 &&
                (filtersIndex <= filtersLimit ||
                  (filtersIndex > filtersLimit && showFilters)) && (
                  <ChipItem
                    chipLabel='Subject'
                    chipValue={filters.subject}
                    onDelete={() => {
                      setFilters(prevFilters => ({
                        ...prevFilters,
                        subject: "",
                      }));
                    }}
                  />
                )}
              <div
                style={{
                  textAlign: "right",
                }}>
                {filtersCount > filtersLimit && (
                  <IconButton
                    color={"primary"}
                    onClick={() => setShowFilter(!showFilters)}>
                    <MoreHoriz />
                  </IconButton>
                )}
              </div>
            </Accordion>
            <div className='userDetailsSearchField inputWrapper filterSelect'>
              <div style={{ marginBottom: "10px" }}>
                <h4>Template Creation Date</h4>
                <FilterCreationDate
                  StartDate={filters.DateFrom}
                  setStartDate={date => {
                    setFilters(prevFilters => ({
                      ...prevFilters,
                      DateFrom: date,
                    }));
                  }}
                  EndDate={filters.DateTo}
                  setEndDate={date => {
                    setFilters(prevFilters => ({
                      ...prevFilters,
                      DateTo: date,
                    }));
                  }}
                />
              </div>
              <InputField
                id='Title'
                name='Title'
                placeholder={"Title"}
                value={filters.title}
                onChange={({ target }) =>
                  setFilters(prevFilters => ({
                    ...prevFilters,
                    title: target.value,
                  }))
                }
              />
              <InputField
                id='Subject'
                name='Subject'
                placeholder={"Subject"}
                value={filters.subject}
                onChange={({ target }) =>
                  setFilters(prevFilters => ({
                    ...prevFilters,
                    subject: target.value,
                  }))
                }
              />
            </div>
          </GridSearchFilter>

          <EmailTemplateGrid
            onOrderBy={onOrderBy}
            order={order}
            templateData={emailTempData}
            loadEmailTemplates={loadEmailTemplates}
            isLoading={isLoading}
            setLoading={setLoading}
            onEditTemplateClick={handleEditTemplateClick}
            onDeleteTemplateClick={handleDeleteTemplate}
            deleteLoading={modalLoading}
          />

          <Pagination
            recordCount={paginationResponse?.totalRecords}
            page={page - 1}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
        </div>
      </div>

      {/* Add Template Modal */}
      <AddEmailTemplateModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onTemplateAdded={handleTemplateSaved}
      />

      {/* Edit Template Modal */}
      <AddEmailTemplateModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedTemplate(null);
        }}
        onTemplateAdded={handleTemplateSaved}
        editTemplateData={selectedTemplate}
      />
    </Dashbard>
  );
};

export default EmailTemplate;
